import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraStatus = 'idle' | 'requesting' | 'live' | 'denied' | 'unavailable';

export const objectCoverSourceRect = (
  sourceWidth: number,
  sourceHeight: number,
  displayWidth: number,
  displayHeight: number
) => {
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    return { sx: 0, sy: 0, sw: 0, sh: 0 };
  }
  if (displayWidth <= 0 || displayHeight <= 0) {
    return { sx: 0, sy: 0, sw: sourceWidth, sh: sourceHeight };
  }

  const scale = Math.max(displayWidth / sourceWidth, displayHeight / sourceHeight);
  const sw = Math.min(sourceWidth, displayWidth / scale);
  const sh = Math.min(sourceHeight, displayHeight / scale);

  return {
    sx: (sourceWidth - sw) / 2,
    sy: (sourceHeight - sh) / 2,
    sw,
    sh
  };
};

const attachStream = (video: HTMLVideoElement | null, stream: MediaStream | null) => {
  if (!video || !stream) {
    return;
  }
  if (video.srcObject !== stream) {
    video.srcObject = stream;
  }
  void video.play().catch(() => undefined);
};

const applyPreferredTrackSettings = async (track: MediaStreamTrack) => {
  const capabilities = (
    typeof track.getCapabilities === 'function' ? track.getCapabilities() : {}
  ) as MediaTrackCapabilities & { focusMode?: string[] };
  const advanced: Array<Record<string, string>> = [];

  if (capabilities.focusMode?.includes('continuous')) {
    advanced.push({ focusMode: 'continuous' });
  }

  if (!advanced.length) {
    return;
  }

  try {
    await track.applyConstraints({ advanced: advanced as unknown as MediaTrackConstraintSet[] });
  } catch {
    return;
  }
};

export const usePassportCamera = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const startingRef = useRef(false);
  const sessionRef = useRef(0);
  const [status, setStatus] = useState<CameraStatus>('idle');

  const stop = useCallback(() => {
    sessionRef.current += 1;
    startingRef.current = false;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setStatus('idle');
  }, []);

  const setVideoRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    attachStream(node, streamRef.current);
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable');
      return;
    }

    if (streamRef.current) {
      attachStream(videoRef.current, streamRef.current);
      setStatus('live');
      return;
    }

    if (startingRef.current) {
      return;
    }

    startingRef.current = true;
    const session = sessionRef.current + 1;
    sessionRef.current = session;
    setStatus('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      if (sessionRef.current !== session) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      const [track] = stream.getVideoTracks();
      if (track) {
        await applyPreferredTrackSettings(track);
      }
      if (sessionRef.current !== session) {
        stream.getTracks().forEach((mediaTrack) => mediaTrack.stop());
        streamRef.current = null;
        return;
      }
      attachStream(videoRef.current, stream);
      setStatus('live');
    } catch (error) {
      if (sessionRef.current !== session) {
        return;
      }
      const name = error instanceof DOMException ? error.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setStatus('denied');
        return;
      }
      setStatus('unavailable');
    } finally {
      startingRef.current = false;
    }
  }, []);

  useEffect(() => () => stop(), [stop]);

  const captureFrame = useCallback((): HTMLCanvasElement | null => {
    const video = videoRef.current;
    if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  }, []);

  return { videoRef: setVideoRef, status, captureFrame, start, stop };
};

export type PassportCamera = ReturnType<typeof usePassportCamera>;
